import React, { createContext, useContext, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { OrganizationType } from '@/types/organization'
import { organizationService } from '@/services/organizations'

interface OrganizationContextType {
  currentOrganization: OrganizationType | null
  organizations: OrganizationType[]
  loading: boolean
  setCurrentOrganization: (org: OrganizationType | null) => void
  setOrganizations: (orgs: OrganizationType[]) => void
  switchOrganization: (slug: string) => void
  refreshOrganizations: () => Promise<void>
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined)

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationType | null>(null)
  const [organizations, setOrganizations] = useState<OrganizationType[]>([])
  const [loading, setLoading] = useState(true)
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const navigate = useNavigate()

  // Load organizations on mount
  useEffect(() => {
    refreshOrganizations()
  }, [])

  // When orgSlug changes, update the current organization
  useEffect(() => {
    if (orgSlug && organizations.length > 0) {
      const org = organizations.find(o => o.slug === orgSlug)
      if (org && org.accepted) {
        setCurrentOrganization(org)
        localStorage.setItem('activeOrganization', org.slug)
      }
    }
  }, [orgSlug, organizations])

  const refreshOrganizations = async () => {
    try {
      setLoading(true)
      const orgs = await organizationService.list()
      setOrganizations(orgs)
      // Store organizations in localStorage for API interceptor
      localStorage.setItem('organizations', JSON.stringify(orgs))
      
      // If we have an orgSlug in the URL, set it as current
      if (orgSlug) {
        const currentOrg = orgs.find(o => o.slug === orgSlug)
        if (currentOrg && currentOrg.accepted) {
          setCurrentOrganization(currentOrg)
          // Save as the active organization
          localStorage.setItem('activeOrganization', orgSlug)
        }
      }
    } catch (error) {
      console.error('Failed to load organizations:', error)
    } finally {
      setLoading(false)
    }
  }

  const switchOrganization = (slug: string) => {
    const org = organizations.find(o => o.slug === slug)
    if (org && org.accepted) {
      setCurrentOrganization(org)
      localStorage.setItem('activeOrganization', slug)
      // Navigate to the same page but with new org
      const currentPath = window.location.pathname
      const pathParts = currentPath.split('/')
      if (pathParts[1] && organizations.some(o => o.slug === pathParts[1])) {
        // Replace the org slug in the path
        pathParts[1] = slug
        // Detail routes (e.g. /:org/integrations/1180/edit, /:org/agents/5)
        // carry a record id scoped to the OLD org; reusing it under the new org
        // 404s. Anything deeper than /:org/:section is record-scoped, so drop
        // to the section root instead of carrying the foreign id across.
        if (pathParts.length > 3) {
          navigate(`/${slug}/${pathParts[2]}`)
        } else {
          navigate(pathParts.join('/'))
        }
      } else {
        // No org in path, go to default page
        navigate(`/${slug}/optimizer`)
      }
    }
  }

  return (
    <OrganizationContext.Provider
      value={{
        currentOrganization,
        organizations,
        loading,
        setCurrentOrganization,
        setOrganizations,
        switchOrganization,
        refreshOrganizations,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider')
  }
  return context
}